import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail, ArrowLeft, Check } from 'lucide-react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth, getStoredFirstVisit } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { phoneAuth, googleAuth, sendEmailOtp, verifyEmailOtp } from '../api/api';
import { track } from '../utils/analytics';
import { getReferral, clearReferral, getInvite, clearInvite } from '../utils/referral';
import { MODAL_BG } from '../assets/modalBg';

const LOGGED_IN_KEY = 'karmana_user_logged_in';

const TRIGGER_CONTENT = {
  interested:       { heading: "You're Almost There", subheading: 'Continue to Join the Activity' },
  attended:         { heading: 'Almost There',         subheading: 'Continue to mark Attended' },
  changemaker_like: { heading: 'Almost There',         subheading: 'Continue to Like the Changemaker' },
  login_button:     { heading: 'Welcome to Karmana',   subheading: 'Continue to Join Karmana' },
  form_submitted:   { heading: 'Almost There',         subheading: 'Continue to Join Karmana' },
  default:          { heading: 'Welcome to Karmana',   subheading: 'Continue to Join Karmana' },
};

// Validators
const isValidPhone = (v) => /^\d{10}$/.test(v);
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const AuthModal = () => {
  const { authModalOpen, authModalTrigger, closeAuthModal, refresh, setUser, triggerOnboarding } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('choose');   // 'choose' | 'otp' | 'fetching'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpType, setOtpType] = useState('phone'); // 'phone' | 'email'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [animPhase, setAnimPhase] = useState(0);
  const [mobileKeyboard, setMobileKeyboard] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);

  // ── Analytics helpers ─────────────────────────────────────────────────────
  const _pendingActivityId = () => {
    try { return JSON.parse(localStorage.getItem('karmana_pending_interest') || 'null')?.activityId || null; } catch { return null; }
  };
  const _pendingOrgId = () => {
    try { return JSON.parse(localStorage.getItem('karmana_pending_like') || 'null')?.changemakerId || null; } catch { return null; }
  };

  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const isReturningUser = !!localStorage.getItem(LOGGED_IN_KEY);
  const content = TRIGGER_CONTENT[authModalTrigger] || TRIGGER_CONTENT.default;

  const validPhone = isValidPhone(phone);
  const validEmail = isValidEmail(email);
  const sendOtpEnabled = validPhone || validEmail;

  // Staggered entrance animation + fetching mode init
  useEffect(() => {
    if (authModalOpen) {
      track('signup_modal_opened', {
        trigger: authModalTrigger,
        activity_id: _pendingActivityId(),
        organisation_id: _pendingOrgId(),
      });
      setAnimPhase(0);
      const t1 = setTimeout(() => setAnimPhase(1), 50);
      const t2 = setTimeout(() => setAnimPhase(2), 550);
      // Start in fetching mode for form_submitted trigger
      if (authModalTrigger === 'form_submitted') {
        setMode('fetching');
      } else {
        setMode('choose');
      }
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [authModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance from fetching → choose after 4 s
  useEffect(() => {
    if (mode === 'fetching') {
      const t = setTimeout(() => setMode('choose'), 4000);
      return () => clearTimeout(t);
    }
  }, [mode]);

  // ── Close / reset ─────────────────────────────────────────────────────────
  const handleClose = () => {
    track('signup_modal_abandoned', {
      step: mode,
      method_selected: mode === 'otp' ? otpType : null,
      trigger: authModalTrigger,
    });
    setMode('choose');
    setPhone('');
    setEmail('');
    setOtp('');
    setError('');
    setBusy(false);
    setAnimPhase(0);
    setMobileKeyboard(false);
    setOtpAttempts(0);
    if (recaptchaRef.current) {
      try { recaptchaRef.current.clear(); } catch { /* ignored */ }
      recaptchaRef.current = null;
    }
    closeAuthModal();
  };

  // ── Shared post-auth handler ──────────────────────────────────────────────
  // Replays pending interest/like, navigates, and triggers new-user onboarding
  // (4 s delay). For 'interested' trigger, success modal drives the 4-sec wait.
  const handleAuthSuccess = (isNew = false) => {
    // Mark pending onboarding for new users (success-modal-driven flows check this flag)
    if (isNew) {
      try { sessionStorage.setItem('karmana_new_user_onboarding', '1'); } catch { /* ignore */ }
    }

    // Pending interest
    try {
      const pending = JSON.parse(localStorage.getItem('karmana_pending_interest') || 'null');
      if (pending?.activityId && pending?.returnPath) {
        localStorage.removeItem('karmana_pending_interest');
        // Interested trigger: success modal will call triggerOnboarding after closing
        if (!isNew || authModalTrigger !== 'interested') {
          if (isNew) triggerOnboarding(4000);
        }
        handleClose();
        navigate(`${pending.returnPath}?autoInterest=${pending.activityId}`);
        return;
      }
    } catch { /* ignored */ }

    // Pending like
    try {
      const pendingLike = JSON.parse(localStorage.getItem('karmana_pending_like') || 'null');
      if (pendingLike?.changemakerId && pendingLike?.returnPath) {
        localStorage.removeItem('karmana_pending_like');
        if (isNew) triggerOnboarding(4000);
        handleClose();
        navigate(`${pendingLike.returnPath}?autoLike=${pendingLike.changemakerId}`);
        return;
      }
    } catch { /* ignored */ }

    // No pending action
    if (isNew) {
      // For 'interested' trigger the success modal handles the 4-sec delay
      if (authModalTrigger !== 'interested') triggerOnboarding(4000);
    }
    handleClose();
  };

  // ── Google ────────────────────────────────────────────────────────────────
  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    setBusy(true);
    try {
      const ft = getStoredFirstVisit();
      const referredBy = getReferral();
      const invite = getInvite();
      const gRes = await googleAuth({
        access_token: tokenResponse.access_token,
        first_time_user_type: ft?.user_type,
        first_time_location: ft?.location,
        referred_by: invite?.invited_by || referredBy || undefined,
        referred_by_activity_id: invite?.activity_id || undefined,
      });
      if (referredBy) clearReferral();
      if (invite) clearInvite();
      localStorage.setItem(LOGGED_IN_KEY, '1');
      track('signup_completed', { method: 'google', is_first_time: gRes?.data?.is_new_user ?? true, user_id: gRes?.data?.user?.id, trigger: authModalTrigger, activity_id: _pendingActivityId() });
      try { await refresh(); } catch { /* ignored */ }
      if (gRes?.data?.user) setUser(gRes.data.user);
      handleAuthSuccess(!!gRes?.data?.is_new_user);
    } catch {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const startGoogleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      track('signup_google_failed', { error_type: 'cancelled_or_api_error', trigger: authModalTrigger });
      setError('Google sign-in was cancelled or failed.');
    },
  });

  // ── Send OTP (phone via Firebase, email via backend) ─────────────────────
  const handleSendOtp = async () => {
    if (!sendOtpEnabled || busy) return;
    setError('');
    setBusy(true);
    try {
      const bothFilled = validPhone && validEmail;
      const _method = (validPhone && !validEmail) ? 'phone' : (bothFilled ? 'email+phone' : 'email');

      if (validPhone && !validEmail) {
        // Phone only → Firebase
        track('signup_method_selected', { method: 'phone', trigger: authModalTrigger, activity_id: _pendingActivityId() });
        if (!recaptchaRef.current) {
          recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
        }
        confirmationRef.current = await signInWithPhoneNumber(auth, `+91${phone}`, recaptchaRef.current);
        setOtpType('phone');
      } else {
        // Email, or both → backend email OTP (phone saved alongside if also entered)
        track('signup_method_selected', { method: bothFilled ? 'email+phone' : 'email', trigger: authModalTrigger, activity_id: _pendingActivityId() });
        await sendEmailOtp({
          email,
          phone: validPhone ? phone : undefined,
        });
        setOtpType('email');
      }
      setMode('otp');
      track('signup_otp_step_reached', { method: _method, trigger: authModalTrigger, activity_id: _pendingActivityId() });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Verify OTP ────────────────────────────────────────────────────────────
  const verifyOtp = async () => {
    if (!otp || busy) return;
    const currentAttempt = otpAttempts + 1;
    setOtpAttempts(currentAttempt);
    track('signup_otp_verify_click', {
      method: otpType,
      trigger: authModalTrigger,
      activity_id: _pendingActivityId(),
      attempt_number: currentAttempt,
    });
    setError('');
    setBusy(true);
    try {
      if (otpType === 'phone') {
        const result = await confirmationRef.current.confirm(otp);
        const idToken = await result.user.getIdToken();
        const ft = getStoredFirstVisit();
        const referredBy = getReferral();
        const invite = getInvite();
        const pRes = await phoneAuth({
          id_token: idToken,
          first_time_user_type: ft?.user_type,
          first_time_location: ft?.location,
          referred_by: invite?.invited_by || referredBy || undefined,
          referred_by_activity_id: invite?.activity_id || undefined,
        });
        if (referredBy) clearReferral();
        if (invite) clearInvite();
        localStorage.setItem(LOGGED_IN_KEY, '1');
        track('signup_completed', { method: 'phone', is_first_time: pRes?.data?.is_new_user ?? true, user_id: pRes?.data?.user?.id, trigger: authModalTrigger, activity_id: _pendingActivityId() });
        try { await refresh(); } catch { /* ignored */ }
        handleAuthSuccess(!!pRes?.data?.is_new_user);
      } else {
        // Email OTP verification
        const ft = getStoredFirstVisit();
        const referredBy = getReferral();
        const invite = getInvite();
        const eRes = await verifyEmailOtp({
          email,
          otp,
          phone: validPhone ? phone : undefined,
          first_time_user_type: ft?.user_type,
          first_time_location: ft?.location,
          referred_by: invite?.invited_by || referredBy || undefined,
          referred_by_activity_id: invite?.activity_id || undefined,
        });
        if (referredBy) clearReferral();
        if (invite) clearInvite();
        localStorage.setItem(LOGGED_IN_KEY, '1');
        track('signup_completed', { method: 'email', is_first_time: eRes?.data?.is_new_user ?? true, user_id: eRes?.data?.user?.id, trigger: authModalTrigger, activity_id: _pendingActivityId() });
        try { await refresh(); } catch { /* ignored */ }
        handleAuthSuccess(!!eRes?.data?.is_new_user);
      }
    } catch (e) {
      track('signup_otp_failed', {
        method: otpType,
        trigger: authModalTrigger,
        activity_id: _pendingActivityId(),
        attempt_number: currentAttempt,
      });
      setError(e?.response?.data?.detail || e?.message || 'Invalid code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleInputFocus = () => { if (window.innerWidth < 768) setMobileKeyboard(true); };
  const handleInputBlur  = () => setMobileKeyboard(false);

  // ── Auto-submit when all 6 OTP digits are entered ────────────────────────
  useEffect(() => {
    if (mode === 'otp' && otp.length === 6 && !busy) verifyOtp();
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authModalOpen) return null;

  // ── Benefits list (shared between choose + otp modes) ────────────────────
  const Benefits = () => (
    <div>
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 border-t border-gray-200" />
      </div>
      <div className="text-center">
        <p className="text-xs text-gray-400 mb-2">Free to Join. Takes 30 seconds.</p>
        <div className="space-y-1">
          {[
            'Discover Interesting Activities',
            'Access Registration Links',
            'Follow Changemakers',
            'Get Updates and Reminders',
          ].map((b) => (
            <p key={b} className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <Check size={13} className="text-green-500 flex-shrink-0" />
              {b}
            </p>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-[100] flex justify-center bg-black/50 p-4 transition-all duration-200 ${
        mobileKeyboard ? 'items-start pt-3' : 'items-center'
      }`}
      data-testid="auth-modal"
    >
      <div
        className="relative rounded-2xl shadow-xl w-full max-w-md p-6 overflow-hidden"
        style={{
          backgroundColor: '#f7f5f0',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* ✕ close */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-gray-300 hover:text-gray-500 transition-colors z-10"
          data-testid="auth-modal-close"
        >
          <X size={18} />
        </button>

        {/* ── Heading + Subheading (Phase 1) ─────────────────────────── */}
        <div
          className="text-center mb-4 mt-2 transition-opacity duration-500"
          style={{ opacity: animPhase >= 1 ? 1 : 0 }}
        >
          <h2 className="text-2xl font-bold text-gray-900">{content.heading}</h2>
          <p className="text-gray-700 text-base mt-1 font-semibold">{content.subheading}</p>
        </div>

        {/* ── Main content (Phase 2) ──────────────────────────────────── */}
        <div
          className="transition-opacity duration-500"
          style={{ opacity: animPhase >= 2 ? 1 : 0, pointerEvents: animPhase >= 2 ? 'auto' : 'none' }}
        >
          {/* ─── FETCHING MODE (Google Forms submission intermediary) ──── */}
          {mode === 'fetching' && (
            <div className="flex flex-col items-center gap-5 py-6" data-testid="auth-fetching-screen">
              {/* Spinner */}
              <div
                className="w-12 h-12 rounded-full border-4 border-green-100"
                style={{
                  borderTopColor: '#16a34a',
                  animation: 'spin 0.9s linear infinite',
                }}
              />
              <div className="text-center">
                <p className="text-base font-bold text-gray-800">Fetching Your Response.</p>
                <p className="text-sm text-gray-500 mt-1">This will only take a moment…</p>
              </div>
            </div>
          )}

          {/* ─── CHOOSE MODE ──────────────────────────────────────────── */}
          {mode === 'choose' && (
            <div>
              {/* Google */}
              <button
                onClick={() => { track('signup_method_selected', { method: 'google', trigger: authModalTrigger, activity_id: _pendingActivityId() }); startGoogleLogin(); }}
                disabled={busy}
                className="w-full flex items-center justify-center gap-3 bg-green-600 text-white rounded-lg py-3 font-medium hover:bg-green-700 transition-colors disabled:opacity-60"
                data-testid="google-login-btn"
              >
                <img src="/images/google-logo.png" alt="" className="w-5 h-5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-gray-300" />
              </div>

              {/* ── Phone field ── */}
              <div className="mb-1">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Phone size={12} className="text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500 font-medium">Continue with Phone (OTP)</span>
                </div>
                <div className="flex rounded-lg border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-green-400 focus-within:border-green-400">
                  <span className="px-3 py-2.5 bg-gray-50 text-gray-500 border-r border-gray-300 text-sm font-medium select-none whitespace-nowrap">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9123456789"
                    className="flex-1 px-3 py-2.5 bg-white focus:outline-none text-sm"
                    data-testid="phone-input"
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>

              {/* OR separator */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">or</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* ── Email field ── */}
              <div className="mb-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Mail size={12} className="text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500 font-medium">Continue with Email ID (OTP)</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  placeholder="yourname@example.com"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  data-testid="email-input"
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>

              {/* Send OTP button — onMouseDown prevents input blur before click fires on mobile */}
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSendOtp}
                disabled={!sendOtpEnabled || busy}
                className={`w-full py-3 rounded-lg font-medium text-sm transition-colors ${
                  sendOtpEnabled && !busy
                    ? 'bg-gray-500 text-white hover:bg-gray-600 active:bg-gray-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="send-otp-btn"
              >
                {busy ? 'Sending…' : 'Send OTP'}
              </button>

              <Benefits />
            </div>
          )}

          {/* ─── OTP MODE ─────────────────────────────────────────────── */}
          {mode === 'otp' && (
            <div>
              {/* Back */}
              <button
                onClick={() => {
                  track('signup_back_to_method', { from_method: otpType, trigger: authModalTrigger });
                  setMode('choose'); setOtp(''); setError('');
                }}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={15} />
                Back
              </button>

              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code sent to{' '}
                <span className="font-semibold text-gray-800">
                  {otpType === 'phone' ? `+91 ${phone}` : email}
                </span>
              </p>

              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full text-center text-2xl tracking-[0.5em] border border-gray-300 rounded-lg py-3 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                data-testid="otp-input"
              />

              <button
                onClick={verifyOtp}
                disabled={busy || otp.length < 6}
                className="w-full mt-3 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-60 transition-colors"
                data-testid="verify-otp-btn"
              >
                {busy ? 'Verifying…' : isReturningUser ? 'Verify & Log In' : 'Verify & Continue'}
              </button>

              {/* Keep benefits to maintain modal height */}
              <Benefits />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm text-center mt-3" data-testid="auth-error">
            {error}
          </p>
        )}

        {/* Karmana logo — always visible */}
        <div className="flex items-center justify-center gap-2 mt-5 pt-3 border-t border-gray-200/60">
          <img
            src="/images/logo.webp"
            alt="Karmana"
            className="w-8 h-8 rounded-full object-contain"
          />
          <div className="text-left">
            <p className="text-sm font-bold text-gray-900 leading-tight">Karmana</p>
            <p className="text-[10px] text-gray-500 leading-tight">Bengaluru&apos;s Civic Action Platform</p>
          </div>
        </div>

        <div id="recaptcha-container" />
      </div>
    </div>
  );
};

export default AuthModal;
